import { HelpAssistTicketInfoTicketAppointmentDto } from "mock-api/src/shared-v2-types";
import { Day } from "src/app/core/day.model";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { UnloaderType } from "src/app/partner/appointments/base-appointment.model";
import { deserializeSimpleCarrier } from "src/app/partner/global/carriers";
import { DoorGroup } from "src/app/partner/settings/door-groups/door-group.model";
import {
  getApiDetailsDecorator,
  getEnumMember,
  parseDateTime,
} from "src/utils";
import { HelpAssistTicketDoor } from "../help-assist-ticket-door.model";
import { HelpAssistTicketAppointmentOrder } from "../ticket-order/help-assist-ticket-appointment-order.model";
import { BaseHelpAssistAppointmentDraftTicket } from "./base-help-assist-ticket-appointment-draft.model";

const api = getApiDetailsDecorator<HelpAssistTicketInfoTicketAppointmentDto>();

interface DeserializeArguments {
  site: Site;
}

type ComputedProperty =
  "carrierId" | "totalCaseCount" | "totalWarehousePalletCount";

export class PartnerHelpAssistTicketAppointmentDraft extends BaseHelpAssistAppointmentDraftTicket {
  private constructor(
    args: Omit<
      ClassProperties<PartnerHelpAssistTicketAppointmentDraft>,
      ComputedProperty
    >,
  ) {
    super(args);

    this.id = args.id;
    this.orders = args.orders;

    // Computed Properties

    this.carrierId = this.carrier?.id ?? null;
    this.totalCaseCount =
      this.orders?.reduce((total, order) => total + order.caseCount, 0) ?? null;

    this.totalWarehousePalletCount = this.getEffectiveTotalWarehousePalletCount(
      this.orders,
    );
  }

  @api() public readonly id: number;
  public readonly orders: HelpAssistTicketAppointmentOrder[] | null;
  public readonly totalCaseCount: number | null;
  public readonly totalWarehousePalletCount: number | null;

  // Include redundant property for backward compatibility on the HA ticket list
  // view table. The carrier column is tied to this property but ideally it
  // would be using the `carrier` property. That will require a data migration
  // to maintain user column configuration, however.
  public carrierId: number | null;

  public static deserialize(
    apiModel: HelpAssistTicketInfoTicketAppointmentDto,
    { site }: DeserializeArguments,
  ): PartnerHelpAssistTicketAppointmentDraft {
    return new PartnerHelpAssistTicketAppointmentDraft({
      carrier: apiModel.carrier
        ? deserializeSimpleCarrier(apiModel.carrier)
        : null,
      deliveryCarrier: apiModel.deliveryCarrierRecord
        ? deserializeSimpleCarrier(apiModel.deliveryCarrierRecord)
        : null,
      door: apiModel.door
        ? HelpAssistTicketDoor.deserialize(apiModel.door)
        : null,
      doorGroup: apiModel.doorGroup
        ? DoorGroup.deserialize(apiModel.doorGroup, { site })
        : null,
      duration: apiModel.duration ?? null,
      id: apiModel.id,
      idealAppointmentDate: apiModel.idealAppointmentDate
        ? Day.deserialize(apiModel.idealAppointmentDate, site.timeZone)
        : null,
      idealStartTime: apiModel.idealStartTime
        ? TimeOfDay.deserialize(apiModel.idealStartTime)
        : null,
      isDropLoad: apiModel.isDropload ?? null,
      isIntermodal: apiModel.isIntermodal ?? null,
      loadWeight: apiModel.loadWeight ?? null,
      mainOrderNumber: apiModel.mainOrderNumber ?? null,
      mainTicketOrderId: apiModel.mainTicketOrderId,
      notificationList: apiModel.notificationList || null,
      orders: HelpAssistTicketAppointmentOrder.deserializeList(
        apiModel.orders,
        { site },
      ),
      scheduledArrivalDay: apiModel.schedule
        ? Day.deserialize(apiModel.schedule, site.timeZone)
        : null,
      site,
      slotStartTime: parseDateTime(apiModel.slotStartTime, site),
      totalWarehousePalletCountOverride:
        apiModel.appointmentPalletOverride ?? null,
      unknownDeliveryCarrierName: apiModel.deliveryCarrier ?? null,
      unloader: getEnumMember(UnloaderType, apiModel.unloader, null),
    });
  }
}

export type HelpAssistTicketAppointmentDraft =
  PartnerHelpAssistTicketAppointmentDraft;
